import { useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Hotel,
  Building2,
  Building,
  Home,
  ShoppingBag,
  Utensils,
  Warehouse,
  Plane,
  Ship,
  TreePine,
  Dumbbell,
  Hospital,
  GraduationCap,
  Store,
  Briefcase,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import {
  editPropertyTypeSchema,
  type EditPropertyTypeData,
} from '@/schema/template';
import { useUpdatePropertyTypeMutation } from '@/hooks/queries/usePropertiesTemplates';
import type { ApiPropertyType } from '@/types/api';

interface EditPropertyTypeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyType: ApiPropertyType | null;
}

// Available icons for property types
const availableIcons = [
  { name: 'Hotel', component: Hotel, label: 'Hotel' },
  { name: 'Building2', component: Building2, label: 'Office Building' },
  { name: 'Building', component: Building, label: 'Commercial Building' },
  { name: 'Home', component: Home, label: 'Residential' },
  { name: 'ShoppingBag', component: ShoppingBag, label: 'Shopping Mall' },
  { name: 'Store', component: Store, label: 'Retail Store' },
  { name: 'Utensils', component: Utensils, label: 'Restaurant' },
  { name: 'Warehouse', component: Warehouse, label: 'Warehouse' },
  { name: 'Plane', component: Plane, label: 'Airport' },
  { name: 'Ship', component: Ship, label: 'Marina/Port' },
  { name: 'TreePine', component: TreePine, label: 'Resort' },
  { name: 'Dumbbell', component: Dumbbell, label: 'Gym/Sports' },
  { name: 'Hospital', component: Hospital, label: 'Hospital/Clinic' },
  {
    name: 'GraduationCap',
    component: GraduationCap,
    label: 'School/University',
  },
  { name: 'Briefcase', component: Briefcase, label: 'Business Center' },
];

// Available colors
const availableColors = [
  { name: 'Blue', hex: '#3B82F6' },
  { name: 'Green', hex: '#10B981' },
  { name: 'Purple', hex: '#8B5CF6' },
  { name: 'Orange', hex: '#F59E0B' },
  { name: 'Red', hex: '#EF4444' },
  { name: 'Pink', hex: '#EC4899' },
  { name: 'Indigo', hex: '#6366F1' },
  { name: 'Teal', hex: '#14B8A6' },
  { name: 'Cyan', hex: '#06B6D4' },
  { name: 'Amber', hex: '#F59E0B' },
];

// Helper function to get hex from color name
const getColorHex = (colorName: string): string => {
  const color = availableColors.find(c => c.name === colorName);
  return color?.hex || '#3B82F6';
};

export function EditPropertyTypeDialog({
  open,
  onOpenChange,
  propertyType,
}: EditPropertyTypeDialogProps) {
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  // React Hook Form with Zod validation
  const form = useForm<EditPropertyTypeData>({
    resolver: zodResolver(editPropertyTypeSchema),
    defaultValues: {
      nameEnglish: '',
      nameArabic: '',
      icon: 'Building2',
      color: 'Blue',
    },
  });

  // Update mutation
  const updateMutation = useUpdatePropertyTypeMutation(propertyType?.id || 0, {
    onSuccessCallback: () => {
      onOpenChange(false);
      form.reset();
    },
  });

  // Update form when propertyType changes
  useEffect(() => {
    if (propertyType && open) {
      form.reset({
        nameEnglish: propertyType.name_en || propertyType.name,
        nameArabic: propertyType.name_ar || propertyType.name,
        icon: propertyType.icon_name || 'Building2',
        color: propertyType.color || 'Blue',
      });
    }
  }, [propertyType, open, form]);

  // Handle form submission
  const onSubmit = (data: EditPropertyTypeData) => {
    if (!propertyType) return;

    updateMutation.mutate({
      name_en: data.nameEnglish,
      name_ar: data.nameArabic,
      icon_name: data.icon,
      color: data.color,
    });
  };

  // Handle dialog close
  const handleClose = () => {
    onOpenChange(false);
    form.reset();
  };

  if (!propertyType) return null;

  const watchedValues = form.watch();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={cn('max-w-3xl w-[90vw]', isRTL && 'rtl')}>
        <DialogHeader className={cn(isRTL && 'text-right')}>
          <DialogTitle>
            {t('templates.admin.editPropertyType.title', 'Edit Property Type')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'templates.admin.editPropertyType.description',
              'Update property type information including name, icon, and color.'
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label
                htmlFor="edit-name-en"
                className={cn(isRTL && 'text-right')}
              >
                {t('templates.admin.editTemplate.nameEnglish')}
              </Label>
              <Controller
                name="nameEnglish"
                control={form.control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="edit-name-en"
                    className={cn(isRTL && 'text-right')}
                    dir="ltr"
                  />
                )}
              />
              {form.formState.errors.nameEnglish && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.nameEnglish.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="edit-name-ar"
                className={cn(isRTL && 'text-right')}
              >
                {t('templates.admin.editTemplate.nameArabic')}
              </Label>
              <Controller
                name="nameArabic"
                control={form.control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="edit-name-ar"
                    dir="rtl"
                    className="text-right"
                  />
                )}
              />
              {form.formState.errors.nameArabic && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.nameArabic.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-icon" className={cn(isRTL && 'text-right')}>
                {t('templates.admin.addPropertyType.icon', 'Icon')}
              </Label>
              <Controller
                name="icon"
                control={form.control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className={cn(isRTL && 'text-right')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      <ScrollArea className="h-full">
                        {availableIcons.map(icon => {
                          const IconComponent = icon.component;
                          return (
                            <SelectItem key={icon.name} value={icon.name}>
                              <div
                                className={cn(
                                  'flex items-center gap-2',
                                  isRTL ? 'flex-row-reverse' : 'flex-row'
                                )}
                              >
                                <IconComponent className="w-4 h-4" />
                                <span>{icon.label}</span>
                              </div>
                            </SelectItem>
                          );
                        })}
                      </ScrollArea>
                    </SelectContent>
                  </Select>
                )}
              />
              {form.formState.errors.icon && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.icon.message}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-color" className={cn(isRTL && 'text-right')}>
                {t('templates.admin.addPropertyType.color', 'Color')}
              </Label>
              <Controller
                name="color"
                control={form.control}
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger className={cn(isRTL && 'text-right')}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      <ScrollArea className="h-full">
                        {availableColors.map(color => (
                          <SelectItem key={color.name} value={color.name}>
                            <div
                              className={cn(
                                'flex items-center gap-2',
                                isRTL ? 'flex-row-reverse' : 'flex-row'
                              )}
                            >
                              <div
                                className="w-4 h-4 rounded-full border"
                                style={{ backgroundColor: color.hex }}
                              />
                              <span>{color.name}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </ScrollArea>
                    </SelectContent>
                  </Select>
                )}
              />
              {form.formState.errors.color && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.color.message}
                </p>
              )}
            </div>

            {/* Preview Section */}
            <div className="col-span-1 md:col-span-2 space-y-2">
              <Label className={cn(isRTL && 'text-right')}>
                {t('templates.admin.addPropertyType.preview', 'Preview')}
              </Label>
              <div className="p-4 border rounded-lg bg-muted/30">
                <div
                  className={cn(
                    'flex items-center gap-3',
                    isRTL ? 'flex-row-reverse' : 'flex-row'
                  )}
                >
                  {watchedValues.icon && (
                    <div
                      className="p-3 rounded-xl text-white shadow-lg"
                      style={{
                        backgroundColor: getColorHex(watchedValues.color),
                      }}
                    >
                      {(() => {
                        const selectedIcon = availableIcons.find(
                          icon => icon.name === watchedValues.icon
                        );
                        if (selectedIcon) {
                          const IconComponent = selectedIcon.component;
                          return <IconComponent className="w-5 h-5" />;
                        }
                        return null;
                      })()}
                    </div>
                  )}
                  <div className={cn(isRTL && 'text-right')}>
                    <div className="font-medium">
                      {watchedValues.nameEnglish || propertyType.name}
                    </div>
                    <div className="text-sm text-muted-foreground" dir="rtl">
                      {watchedValues.nameArabic || propertyType.name}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className={cn(isRTL && 'flex-row-reverse')}>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={updateMutation.isPending}
            >
              {t('templates.admin.editTemplate.cancel')}
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending
                ? t('common.saving', 'Saving...')
                : t('templates.admin.editTemplate.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
